// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useTheme } from '@azure/communication-react';
import { mergeStyles, Stack, Text } from '@fluentui/react';
import { PromptStarter } from 'components/PromptStarter';
import { PushToTalkButton, PushToTalkButtonState } from 'components/PushToTalkButton';
import React, { useCallback, useEffect } from 'react';
import { SpeakToBotSessionManager } from 'utils/speakToBotUtils';
import { BotServerError } from './BotServerError';

export const SpeakToBot = (props: { sessionManager: SpeakToBotSessionManager }): JSX.Element => {
  const theme = useTheme();

  const { sessionManager } = props;
  const connectedToBotSession = React.useRef<boolean>(false);
  const [talkingState, setTalkingState] = React.useState<PushToTalkButtonState>('stopped');
  const [serverError, setServerError] = React.useState<boolean>(false);
  const serverErrorEvent = useCallback(() => {
    setServerError(true);
  }, [sessionManager]);

  const introText = talkingState === 'talking' ? "I'm listening" : 'Click to start talking';

  useEffect(() => {
    sessionManager.on('serverError', serverErrorEvent);
    return () => {
      sessionManager.off('serverError', serverErrorEvent);
    };
  }, [sessionManager, serverErrorEvent]);

  if (serverError) {
    return <BotServerError />;
  }

  return (
    <Stack verticalFill verticalAlign="center" styles={{ root: { background: theme.palette.neutralLighter } }}>
      <Stack tokens={{ childrenGap: '2rem' }} horizontalAlign="center">
        <Text className={introTextStyle}>{introText}</Text>
        <PushToTalkButton
          talkOn={async () => {
            if (!connectedToBotSession.current) {
              try {
                connectedToBotSession.current = true;
                await sessionManager?.connect();
              } catch (e) {
                connectedToBotSession.current = false;
                console.error('Error connecting to bot session:', e);
                setServerError(true);
                return;
              }
            } else {
              await sessionManager?.unmute();
            }
          }}
          talkOff={sessionManager?.mute}
          onStateChange={setTalkingState}
        />
        <Stack tokens={{ childrenGap: '1rem' }} horizontalAlign="center">
          <PromptStarter body="What is Azure Communication Services?" icon="🎤" />
          <PromptStarter body="Give me directions from Seattle to Vancouver by Car" icon="🎤" />
          <PromptStarter body="Tell me a recipe using Jalapeños" icon="🎤" />
        </Stack>
      </Stack>
    </Stack>
  );
};

const introTextStyle = mergeStyles({
  fontSize: '1.25rem',
  fontWeight: '400'
});
